import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowsClockwise, UsersThree, MapPin, MagnifyingGlass, DownloadSimple } from "@phosphor-icons/react";

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteFilter, setSiteFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [synced, setSynced] = useState(false);
  const [syncReport, setSyncReport] = useState(null);

  const load = useCallback(async () => {
    try {
      const [cRes, sRes] = await Promise.all([
        api.get("/customers", { params: siteFilter !== "all" ? { site: siteFilter } : {} }),
        api.get("/sites"),
      ]);
      setCustomers(cRes.data);
      setSites(sRes.data);
    } catch (e) {
      /* silent */
    }
  }, [siteFilter]);

  useEffect(() => {
    load();
  }, [load]);

  async function sync() {
    setSyncing(true);
    setSynced(false);
    try {
      const { data } = await api.post("/uisp/sync");
      setSyncReport(data);
      toast.success(
        `Synced ${data.synced} of ${data.total_from_uisp} UISP clients` +
          (data.skipped_inactive ? ` · ${data.skipped_inactive} inactive (no active subscription)` : "") +
          (data.skipped_no_phone ? ` · ${data.skipped_no_phone} had no phone` : "") +
          (data.merged_duplicate_phone ? ` · ${data.merged_duplicate_phone} merged (shared phone)` : "") +
          (data.removed ? ` · ${data.removed} removed (stale)` : "")
      );
      setSynced(true);
      setTimeout(() => setSynced(false), 2500);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSyncing(false);
    }
  }

  async function exportNoPhone() {
    try {
      const { data } = await api.get("/uisp/nophone");
      if (!data || data.length === 0) {
        toast.info("No no-phone clients to export");
        return;
      }
      const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const header = ["UISP Client ID", "Name", "Email", "Lists/Tags"];
      const rows = data.map((d) =>
        [esc(d.uisp_id), esc(d.name), esc(d.email), esc((d.tags || []).join("; "))].join(",")
      );
      const csv = [header.join(","), ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `uisp-no-phone-clients-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.length} no-phone clients`);
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  const filtered = customers.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.phone.includes(search)
  );

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="h-16 flex items-center px-6 border-b border-border gap-3">
        <UsersThree size={22} weight="fill" className="text-primary" />
        <h1 className="text-xl font-semibold tracking-tight">Customers</h1>
        <span className="text-xs text-muted-foreground font-mono">{customers.length}</span>
        <Button
          data-testid="uisp-sync-button"
          onClick={sync}
          disabled={syncing}
          className={`ml-auto rounded-sm gap-2 transition-colors ${
            synced
              ? "bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))] text-white"
              : "bg-primary hover:bg-primary/90 text-primary-foreground"
          }`}
        >
          <ArrowsClockwise size={16} weight="bold" className={syncing ? "animate-spin" : ""} />
          {syncing ? "Syncing…" : synced ? "Synced" : "Sync from UISP"}
        </Button>
      </div>

      <div className="px-6 py-4 border-b border-border flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="customer-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or phone…"
            className="rounded-sm pl-9"
          />
        </div>
        <Select value={siteFilter} onValueChange={setSiteFilter}>
          <SelectTrigger className="rounded-sm w-56" data-testid="customer-site-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All lists</SelectItem>
            {sites.map((s) => (
              <SelectItem key={s.name} value={s.name}>
                {s.name} · {s.count}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {syncReport && (
        <div
          data-testid="sync-report"
          className="mx-6 mt-4 border border-border rounded-sm bg-card/40 p-4 text-sm"
        >
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <span className="text-muted-foreground">UISP clients: <span className="text-foreground font-mono">{syncReport.total_from_uisp}</span></span>
            <span className="text-muted-foreground">Imported new: <span className="text-foreground font-mono">{syncReport.imported}</span></span>
            <span className="text-muted-foreground">Updated: <span className="text-foreground font-mono">{syncReport.updated}</span></span>
            <span className="text-muted-foreground">Inactive (skipped): <span className="text-foreground font-mono">{syncReport.skipped_inactive ?? 0}</span></span>
            <span className="text-muted-foreground">No phone (skipped): <span className="text-foreground font-mono">{syncReport.skipped_no_phone}</span></span>
            <span className="text-muted-foreground">Merged (shared phone): <span className="text-foreground font-mono">{syncReport.merged_duplicate_phone}</span></span>
            <span className="text-muted-foreground">Removed (stale): <span className="text-foreground font-mono">{syncReport.removed ?? 0}</span></span>
            <span className="text-muted-foreground">Total in app: <span className="text-primary font-mono font-semibold">{syncReport.total_customers}</span></span>
          </div>
          {syncReport.skipped_no_phone > 0 && (
            <div className="mt-3">
              <Button
                data-testid="export-nophone-button"
                onClick={exportNoPhone}
                variant="secondary"
                size="sm"
                className="rounded-sm gap-2"
              >
                <DownloadSimple size={15} weight="bold" />
                Export no-phone clients ({syncReport.skipped_no_phone}) as CSV
              </Button>
            </div>
          )}
          {syncReport.skipped_samples?.length > 0 && (
            <div className="mt-3 text-xs text-muted-foreground">
              <span className="uppercase tracking-widest">No-phone clients (sample):</span>{" "}
              {syncReport.skipped_samples.join(", ")}
              {syncReport.skipped_no_phone > syncReport.skipped_samples.length &&
                ` … +${syncReport.skipped_no_phone - syncReport.skipped_samples.length} more`}
            </div>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            Only clients with an active or suspended subscription are imported. SMS threads are keyed by phone number, so multiple UISP clients sharing one number are merged into a single contact.
          </p>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            No customers yet. Configure UISP in Settings, then click{" "}
            <span className="text-foreground">Sync from UISP</span>.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card border-b border-border">
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                <th className="px-6 py-3 font-semibold">Name</th>
                <th className="px-6 py-3 font-semibold">Phone</th>
                <th className="px-6 py-3 font-semibold">Lists / Tags</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr
                  key={c.id}
                  data-testid={`customer-row-${c.id}`}
                  className="border-b border-border hover:bg-accent/50 transition-colors"
                >
                  <td className="px-6 py-3 font-medium">{c.name}</td>
                  <td className="px-6 py-3 font-mono text-muted-foreground">{c.phone}</td>
                  <td className="px-6 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {(c.tags && c.tags.length > 0) ? (
                        c.tags.map((t) => (
                          <span
                            key={t}
                            className="inline-flex items-center gap-1 text-xs bg-secondary border border-border px-2 py-0.5 rounded-sm"
                          >
                            <MapPin size={11} /> {t}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
